import { StepForm } from "Components/StepForm";
import { useEffect, useState } from "react";
import { ProgramAndCountry } from "./ProgramAndCountry";
import { ApplyingFor } from "./ApplyingFor";
import { OtherPreferences } from "./OtherPreferences";
import { TestScores } from "./TestScores";
import { Flex, Form, notification, Spin, Typography } from "antd";
import { EducationAndExperience } from "./EducationAndExperience";
import { useNavigate, useSearchParams } from "react-router-dom";
import { registerNewUserViaGoogle } from "Features/Auth/apis/registerUser";
import {
  findCollegesApi,
  updateCollegeFinderDetails,
} from "./apis/collegeFinderForm";
import { useMutation } from "@tanstack/react-query";
import { useUserInfo } from "GlobalStates/User";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import { ROUTES } from "Features/Routes/routeConstants";
import { useRecoilValue } from "recoil";
import { cookieConsentState } from "GlobalStates/CookieConsent";
import ReactGA from "react-ga4";
import "./styles.css";

const steps = [
  {
    title: "Program & Country",
  },
  {
    title: "Applying For",
  },
  {
    title: "Education",
  },
  {
    title: "Test Scores",
  },
  {
    title: "Preferences",
  },
];

export const CollegeFinder = () => {
  const { Title } = Typography;
  const [api, contextHolder] = notification.useNotification();
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const navigate = useNavigate();
  const { user, setUser } = useUserInfo();
  const [form] = Form.useForm();
  const consent = useRecoilValue(cookieConsentState);
  const [searchParams] = useSearchParams();

  const stepFormElements = [
    <ProgramAndCountry form={form} />,
    <ApplyingFor form={form} />,
    <EducationAndExperience form={form} />,
    <TestScores form={form} />,
    <OtherPreferences form={form} />,
  ];
  const stepFormTitles = [
    "Program & Country",
    "Applying For",
    "Previous Education & Experience",
    "Test Scores",
    "Other Preferences",
  ];

  const generateRecommendationsMutation = useMutation({
    mutationFn: findCollegesApi,
  });

  const registerUserMutation = useMutation({
    mutationFn: registerNewUserViaGoogle,
    onSuccess: async (data) => {
      try {
        ReactGA.event("login", {
          method: "google",
        });
        setLoadingRecommendations(true);
        await generateRecommendationsMutation.mutateAsync();
        setUser({
          token: data?.data?.accessToken,
          tier: data?.data?.tier,
          completedCollegeFinder: data?.data?.completedCollegeFinder,
          name: data?.data?.name,
          picture: data?.data?.picture,
          id: data?.data?.id,
          email: data?.data?.email,
        });
        if (data?.data?.message === "user already exists") {
          toast.success("Welcome back " + data?.data?.name);
        } else if (data?.data?.message === "user created") {
          toast.success("Welcome " + data?.data?.name);
        }
      } catch (e) {
        toast.error("Something went wrong while generating recommendations");
      } finally {
        setLoadingRecommendations(false);
      }
    },
    onError: (error) => {
      api.open({
        message: "Oops! We have a problem",
        duration: 5,
      });
    },
  });

  const googleLogin = useGoogleLogin({
    onSuccess: (user) => {
      registerUserMutation.mutate({
        token: user?.access_token,
        preferences: form.getFieldsValue(true),
      });
    },
  });

  const updateCollegeFinderMutation = useMutation({
    mutationFn: updateCollegeFinderDetails,
    onSuccess: async (data) => {
      try {
        setLoadingRecommendations(true);
        await generateRecommendationsMutation.mutateAsync();
        setUser((prev) => ({
          ...prev,
          completedCollegeFinder: true,
        }));
        navigate(ROUTES.DASHBOARD);
      } catch (e) {
        toast.error("Something went wrong while generating recommendations");
      } finally {
        setLoadingRecommendations(false);
      }
    },
    onError: (error) => {
      api.open({
        message: "Oops! We have a problem",
        duration: 5,
      });
    },
  });

  const updateCollegeFinderPartial = useMutation({
    mutationFn: updateCollegeFinderDetails,
    onError: (error) => {
      api.open({
        message: "Oops! We have a problem",
        duration: 5,
      });
    },
  });

  useEffect(() => {
    if (user?.token && user?.completedCollegeFinder) {
      if (searchParams?.get("next")) {
        navigate(searchParams?.get("next"), { replace: true, state: {} });
      } else {
        navigate(ROUTES.DASHBOARD);
      }
    }
  }, [user, navigate, searchParams]);

  const onFinish = async () => {
    try {
      await form.validateFields();
      if (!consent) {
        toast.error("Please allow cookies to use this feature");
        return;
      }
      if (!user?.token) {
        googleLogin();
      } else {
        updateCollegeFinderMutation.mutate({
          preferences: form.getFieldsValue(true),
        });
      }
    } catch (error) {}
  };

  if (loadingRecommendations) {
    return (
      <Flex className="w-full h-full justify-center items-center">
        <Spin size="large" />
      </Flex>
    );
  }
  return (
    <Flex vertical style={{ width: "100%", marginTop: "50px" }} align="center">
      {contextHolder}
      <Title level={2}>{stepFormTitles[currentStep]}</Title>
      <Form form={form} className="college-finder-form">
        <StepForm
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          content={stepFormElements[currentStep]}
          onFinish={onFinish}
          form={form}
          onNext={
            user?.token
              ? (fieldValues) => {
                  ReactGA.send({
                    hitType: "pageview",
                    page: `/college-finder/${currentStep}`,
                  });
                  updateCollegeFinderPartial.mutate({
                    preferences: fieldValues,
                  });
                }
              : undefined
          }
        ></StepForm>
      </Form>
    </Flex>
  );
};
